import AudioOptionButton from "../VoiceControl/AudioOptionButton";
import { ReactComponent as MusicNoteIcon } from "assets/musicNote.svg";
import { AudioOptionButtonState } from "../VoiceControl/AudioOptionButton/AudioOptionButton.const";
import {
	Container,
	SliderContainer,
	Title,
	SliderIndicator,
	SliderIndicatorContainer,
} from "./Tonality.style";
import { Button, Slider } from "@mui/material";
import { useEffect, useState } from "react";
import Popover from "@mui/material/Popover";
import { useDispatch, useSelector } from "react-redux";
import { colorsSelector } from "@redux/colorPalette/selectors";
import { MUSIC_PITCH } from "constants/vocalPresets";
import { useMusicPitchValueSelector, setMusicPitchValue } from "@redux/player";
import { FormattedMessage } from "react-intl";
import analytics from "lib/analytics";

export function Tonality() {
	const dispatch = useDispatch();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const colors = useSelector(colorsSelector);
	const [m_output1, set_m_output1] = useState<any>(null);
	const musicPicthValue = useMusicPitchValueSelector();

	let m = null;

	const onMIDISuccess = (midiAccess: any) => {
		m = midiAccess;
		const outputs = m.outputs;

		const iteratorOutputs = outputs.values();

		set_m_output1(iteratorOutputs.next().value);
	};

	const onMIDIFailure = () => {
		console.log("Could not access your MIDI devices.");
	};

	const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		setAnchorEl(event.currentTarget);
		analytics.track("Tonality_Opened");
	};

	const handleClose = () => {
		setAnchorEl(null);
		analytics.track("Tonality_Closed");
	};

	const open = Boolean(anchorEl);
	const id = open ? "tonality-popover" : undefined;

	const handleMusicPitchSliderChange = (
		_event: React.SyntheticEvent | Event,
		value: number | number[],
	) => {
		dispatch(setMusicPitchValue(value as number));
	};

	useEffect(() => {
		analytics.track("Tonality", { value: musicPicthValue });
		const pitchValue = MUSIC_PITCH[String(musicPicthValue)] ?? MUSIC_PITCH["0"];
		m_output1?.send([0xb0, 0x01, pitchValue], 0x7f);
	}, [musicPicthValue]);

	useEffect(() => {
		navigator.requestMIDIAccess().then(onMIDISuccess, onMIDIFailure);
	}, []);

	const marks = Object.keys(MUSIC_PITCH).map((key) => ({
		value: Number.parseInt(key),
		label: key,
	}));

	return (
		<Container>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
			>
				<SliderContainer>
					<Title>
						<FormattedMessage id="voice-effects.music-effects.title" />
					</Title>
					<SliderIndicator>
						<FormattedMessage id="voice-effects.music-effects.high" />
					</SliderIndicator>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
						}}
					>
						<Button
							variant="contained"
							size="small"
							sx={{
								background: "white",
								backgroundColor: colors.highlightPoint,
								borderRadius: "5px",
								marginBottom: "10px",
								marginRight: "10px",
								height: "20px",
								width: "30px",
								padding: "0",
								margin: "0",
								":hover": {
									backgroundColor: colors.highlightPoint,
								},
							}}
							onClick={() => {
								analytics.track("Tonality_Reset");
								dispatch(setMusicPitchValue(0));
							}}
						>
							Reset
						</Button>
						<SliderIndicatorContainer>
							<Slider
								aria-label="Tonalité"
								sx={{
									height: 200,
									color: colors.highlightPoint,
									"& .MuiSlider-markLabel": {
										color: "gray",
										fontSize: "11px",
									},
									"& .MuiSlider-mark": {
										width: "8px",
										height: "2px",
										backgroundColor: "gray",
									},
									"& .MuiSlider-track": {
										color: colors.highlightPoint,
										background: colors.highlightPoint,
									},
									"& .MuiSlider-rail": {
										color: colors.highlightPoint,
										background: colors.highlightPoint,
										opacity: 1,
									},
								}}
								defaultValue={musicPicthValue}
								valueLabelDisplay="off"
								step={1}
								min={-4}
								max={4}
								orientation="vertical"
								marks={marks}
								onChangeCommitted={handleMusicPitchSliderChange}
								value={musicPicthValue}
							/>
						</SliderIndicatorContainer>
					</div>
					<SliderIndicator>
						<FormattedMessage id="voice-effects.music-effects.low" />
					</SliderIndicator>
				</SliderContainer>
			</Popover>

			<AudioOptionButton
				aria-describedby={id}
				Icon={MusicNoteIcon}
				translationId="control-bar.tonalite"
				buttonState={
					musicPicthValue === 0
						? AudioOptionButtonState.INACTIVE
						: AudioOptionButtonState.ACTIVE
				}
				onClick={(e) => handleClick(e)}
			/>
		</Container>
	);
}
